import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { Container } from "react-bootstrap";
import { CheckCircle } from "lucide-react";
import "../../../assets/css/seller/SupportInSeller/SupportInSeller.css";
import SellerMasterLayout from "../../../layout/seller/SellerMasterLayout";
import AppContext from "../../../context/AppContext";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

/* ── Icons ── */
const IconChat = () => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    width="15"
    height="15"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2 5a2 2 0 012-2h12a2 2 0 012 2v7a2 2 0 01-2 2H7l-4 3V5z"
    />
  </svg>
);

const IconPlus = () => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    width="14"
    height="14"
  >
    <path strokeLinecap="round" d="M10 3v14M3 10h14" />
  </svg>
);

const IconX = () => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    width="14"
    height="14"
  >
    <path strokeLinecap="round" d="M4 4l12 12M16 4L4 16" />
  </svg>
);

/* ── Helpers ── */
const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

/* ── Small reusable error text ── (✅ NEW) */
const FieldError = ({ message }) =>
  message ? (
    <p
      style={{
        color: "#dc3545",
        fontSize: "0.8em",
        marginTop: "4px",
        marginBottom: 0,
      }}
    >
      {message}
    </p>
  ) : null;

/* ── Status Badge ── */
const StatusBadge = ({ status }) => {
  const map = {
    Open: "open",
    Resolved: "resolved",
    Closed: "closed",
    Pending: "pending",
  };
  return (
    <span className={`sp-badge sp-badge--${map[status] || "closed"}`}>
      {status}
    </span>
  );
};

/* ── Chat Button ── */
const ChatBtn = ({ ticketId }) => {
  const navigate = useNavigate();
  return (
    <button
      className="sp-btn-chat"
      onClick={() => navigate(`/seller-chat/${ticketId}`)}
    >
      <IconChat />
    </button>
  );
};

/* ── Raise Ticket Modal ── */
/* ✅ resolveTicket / resolvingId / disputes / tickets state REMOVED from here —
   that logic belongs to the parent (SupportInSeller), not the "raise a new ticket" modal. */
const RaiseTicketModal = ({ show, onClose, onTicketAdded }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const { baseUrl, loggedInSeller, regTpChecker } = useContext(AppContext); // ✅ regTpChecker added

  // ✅ NEW — error state
  const [errors, setErrors] = useState({});

  // ✅ NEW — validate ticket form
  const validateTicketForm = () => {
    const newErrors = {};

    const titleErr = regTpChecker("ticketTitle", title);
    if (titleErr) newErrors.title = titleErr;

    const descErr = regTpChecker("ticketDescription", description);
    if (descErr) newErrors.description = descErr;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const SubmitTicket = () => {
    if (!validateTicketForm()) return; // ✅ NEW guard

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("generated_by", "shop");
    formData.append("tpshop_id", loggedInSeller.id);

    axios
      .post(`${baseUrl}/seller/general/helpSupportSeller`, formData, {
        headers: { "Content-type": "multipart/formData" },
      })
      .then(() => {
        Swal.fire({
          icon: "success",
          title: "Ticket Submitted",
          text: "Your support ticket has been submitted successfully.",
          confirmButtonText: "OK",
        });
        setTitle("");
        setDescription("");
        setErrors({}); // ✅ NEW — clear errors after successful submit
        onClose();
        if (onTicketAdded) onTicketAdded();
      })
      .catch((err) => console.log(err));
  };

  if (!show) return null;

  return (
    <div
      className="sp-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="sp-modal">
        <div className="sp-modal__head">
          <span className="sp-modal__title">Raise a Ticket</span>
          <button className="sp-modal__close" onClick={onClose}>
            <IconX />
          </button>
        </div>
        <div className="sp-modal__body">
          <div className="sp-form-group">
            <label className="sp-label">Title</label>
            <input
              className="sp-input"
              style={errors.title ? { borderColor: "#dc3545" } : undefined} // ✅ NEW
              placeholder="e.g. Issue with order payment"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setErrors((prev) => ({ ...prev, title: "" })); // ✅ NEW
              }}
            />
            <FieldError message={errors.title} /> {/* ✅ NEW */}
          </div>
          <div className="sp-form-group">
            <label className="sp-label">Description</label>
            <textarea
              className="sp-textarea"
              style={
                errors.description ? { borderColor: "#dc3545" } : undefined
              } // ✅ NEW
              placeholder="Describe your issue in detail..."
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setErrors((prev) => ({ ...prev, description: "" })); // ✅ NEW
              }}
            />
            <FieldError message={errors.description} /> {/* ✅ NEW */}
          </div>
        </div>
        <div className="sp-modal__foot">
          <button className="sp-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="sp-btn-primary" onClick={SubmitTicket}>
            <IconPlus /> Submit Ticket
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Customer Disputes Table ── */
/* ✅ now receives resolveTicket + resolvingId from parent, and renders a Resolve button
   alongside the Chat button */
const CustomerDisputes = ({ disputes, resolveTicket, resolvingId }) => (
  <div className="sp-table-card">
    <table className="sp-table">
      <thead>
        <tr>
          <th>Dispute</th>
          <th>Order</th>
          <th>Customer</th>
          <th>Reason</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {disputes.length === 0 ? (
          <tr>
            <td colSpan={6} className="sp-empty">
              No customer disputes yet.
            </td>
          </tr>
        ) : (
          disputes.map((d) => (
            <tr key={d.id}>
              <td>{d.id}</td>
              <td>{d.order_id}</td> {/* order_id */}
              <td>{d.tpcustomer_id}</td> {/* ya customer name agar relation use karna ho */}
              <td>{d.title}</td>
              <td>
                <StatusBadge status={d.status} />
              </td>
              <td>
                <div className="sp-action-buttons">
                  <ChatBtn ticketId={d.id} />

                  <button
                    className="sp-btn-resolve"
                    disabled={
                      String(d.status || "").toLowerCase() === "resolved" ||
                      resolvingId === d.id
                    }
                    onClick={() => resolveTicket(d.id)}
                    title="Resolve"
                  >
                    {resolvingId === d.id ? "..." : <CheckCircle size={15} />}
                  </button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

/* ── My Tickets Table ── */
const MyTickets = ({ tickets }) => (
  <div className="sp-table-card">
    <table className="sp-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>Title</th>
          <th>Description</th>
          <th>Status</th>
          <th>Date</th>
          <th>Chat</th>
        </tr>
      </thead>
      <tbody>
        {tickets.length === 0 ? (
          <tr>
            <td colSpan={6} className="sp-empty">
              No tickets raised yet.
            </td>
          </tr>
        ) : (
          tickets.map((t) => (
            <tr key={t.id}>
              <td className="sp-cell-mono">{t.id}</td>
              <td>{t.title}</td>
              <td className="sp-cell-desc">{t.description}</td>
              <td>
                <StatusBadge status={t.status} />
              </td>
              <td>{t.created_at}</td>
              <td>
                <ChatBtn ticketId={t.id} />
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

/* ── Main Page ── */
const SupportInSeller = () => {
  const [tickets, setTickets] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [resolvingId, setResolvingId] = useState(null); // ✅ moved here from modal

  const [activeTab, setActiveTab] = useState("disputes");
  const [showModal, setShowModal] = useState(false);
  const { baseUrl, loggedInSeller } = useContext(AppContext);

  const viewMyTickets = () => {
    if (!loggedInSeller?.id) return;
    const fd = new FormData();
    fd.append("tpshop_id", loggedInSeller.id);
    axios
      .post(`${baseUrl}/seller/general/viewMyTickets`, fd, {
        headers: { "Content-Type": "multipart/formData" },
      })
      .then((res) => setTickets(res.data.tickets))
      .catch((err) => console.log(err));
  };

  const viewMyCustomerTickets = () => {
    if (!loggedInSeller?.id) return;

    const fd = new FormData();
    fd.append("tpshop_id", loggedInSeller.id);

    axios
      .post(`${baseUrl}/seller/general/viewMyCustomerTickets`, fd, {
        headers: { "Content-Type": "multipart/formData" },
      })
      .then((res) => {
        setDisputes(res.data.tickets);
      })
      .catch((err) => console.log(err));
  };

  // ✅ moved here from RaiseTicketModal — this is the correct owner of `disputes`/`setDisputes`
  const resolveTicket = async (ticketId) => {
    const result = await Swal.fire({
      title: "Resolve Dispute?",
      text: "Are you sure this customer dispute has been solved?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Resolve",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setResolvingId(ticketId);

      const formData = new FormData();

      formData.append("id", ticketId);
      formData.append("tpshop_id", loggedInSeller.id);

      const response = await axios.post(
        `${baseUrl}/seller/general/resolveTicket`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.success) {
        setDisputes((prev) =>
          prev.map((ticket) =>
            ticket.id === ticketId
              ? { ...ticket, status: "resolved" }
              : ticket
          )
        );

        Swal.fire({
          icon: "success",
          title: "Resolved",
          text: "Customer dispute resolved successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data?.message || "Unable to resolve dispute.",
        });
      }
    } catch (error) {
      console.log("Seller Resolve Error:", error?.response?.data || error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Unable to resolve dispute.",
      });
    } finally {
      setResolvingId(null);
    }
  };

  useEffect(() => {
    viewMyTickets();
    viewMyCustomerTickets();
  }, []);

  return (
    <SellerMasterLayout>
      <div className="sp-page">
        <Container>
          {/* ── Page Header ── */}
          <div className="sp-page-head">
            <div>
              <h1 className="sp-heading">Support &amp; Disputes</h1>
              <p className="sp-subheading">
                Resolve customer disputes and raise tickets to admin
              </p>
            </div>
            <button
              className="sp-btn-primary"
              onClick={() => setShowModal(true)}
            >
              <IconPlus /> Raise Ticket
            </button>
          </div>

          {/* ── Tabs ── */}
          <div className="sp-tabs">
            <button
              className={`sp-tab ${activeTab === "disputes" ? "sp-tab--active" : ""}`}
              onClick={() => setActiveTab("disputes")}
            >
              Customer Disputes ({disputes.length})
            </button>
            <button
              className={`sp-tab ${activeTab === "tickets" ? "sp-tab--active" : ""}`}
              onClick={() => setActiveTab("tickets")}
            >
              My Tickets ({tickets.length})
            </button>
          </div>

          {/* ── Tab Content ── */}
          {activeTab === "disputes" ? (
            <CustomerDisputes
              disputes={disputes}
              resolveTicket={resolveTicket}
              resolvingId={resolvingId}
            />
          ) : (
            <MyTickets tickets={tickets} />
          )}
        </Container>
      </div>

      {/* ── Modal ── */}
      <RaiseTicketModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onTicketAdded={viewMyTickets}
      />
    </SellerMasterLayout>
  );
};

export default SupportInSeller;